// §4.1 — CUSTOMER LIST, /sales/customers (Module 09, slice P3-A part 2;
// wave-1b sort retrofit in P3-C-0; saved views in PR-E4, D-305).
//
// The book: one row per customer, server-sorted (D-242 — default
// `-created_at`; the wire's typed `order` enum, never a client re-sort of a
// partial cursor window), cursor pages folded through the shared accumulator
// (lib/paged-rows.ts — fold-by-id, so a PATCH's refetch UPDATES a row instead
// of throwing the update on the floor). Search is SERVER-side (`q` matches
// name/customer_no/tax_id, and a phone or channel address for a holder of
// `messaging.address.read`, D-307), debounced 250 ms so a typed name costs one
// request, not one per keystroke.
//
// THE ADDRESS IS THE WHOLE STATE (PR-E4, D-305 rulings 3 and 17). The search
// box and the four filters live in the URL beside the sort, as `q`, `tier`,
// `status`, `owner`, `credit_hold` and `order`, with `view` naming a saved
// view (screens/search-params.ts). A default is an absent parameter, so a bare
// `/sales/customers` sends the request it always sent. Every filter and sort
// write goes through the saved-views controller's `write`, which judges the
// order-token rule while a view is active and passes a write with no `view`
// through untouched. The box keeps its own text for typing and follows the
// address whenever something else moves `q` (a view applied, All, Reset).
// A filter or sort change resets the cursor stack and the fold exactly as it
// always did: the old pages were minted under a query that no longer exists,
// and the server refuses their cursors (400) rather than trusting us.
//
// THE CARET SET (the wave-1b adjudication): display_name, customer_no,
// reliability_score and Created, and since PR-E4 Updated, a column hidden by
// default that a reader shows through the column chooser (spec 09 §4.1). No
// caret on tier/status (the raw enum under a translated chip is a dishonest
// order), owner/open-AR (not wire columns) or credit (§7-REDACTED by omission:
// a sort on it would leak relative magnitude through row order to the very
// roles the redaction hides the value from).
//
// COLUMNS ARE HONEST TO THE WIRE:
//   * reliability — the §3.9 chip (green ≥ 80 / amber 50–79 / red < 50), with
//     `data-provisional` ALWAYS "true" in P3 (§3.9's stated degradation;
//     seeded scores included). A null score renders the register's absent
//     mark, never an empty cell.
//   * open AR — §3.5b's GLOBAL degradation is a per-tenant fact, so while no
//     row carries `open_ar_minor` the column is absent and the line above the
//     table carries the "AR unavailable until module 11" chip once (D-336),
//     instead of a column of identical chips or faked zeros. The column
//     returns by itself when module 11 adds per-row AR.
//   * credit — the hold chip (always-present boolean), the prepay-only chip
//     (limit === 0 AND the key present), or the limit itself; when
//     `credit_limit_minor` is §7-OMITTED the cell renders NOTHING — never a
//     dash pretending to be data (lib/money.ts header states the rule). The
//     column is STRUCTURAL for the chooser (D-305 ruling 8): it carries the
//     credit-hold chip, which every reader is sent, so it exists on every page
//     and a reader may hide it.
//   * open-orders count — DECLARED ABSENT until P3-C (no wire field; stated
//     in the §4.1 annotation), so there is no column to lie with.
//
// SAVED VIEWS (spec 16 §3.12 and §4). The switcher sits in the filter row and
// the column chooser behind its Columns control; the switcher row and the
// table are one content region, revealed once the views read and the first
// page have both settled (design-system §5). A shared view never keeps a
// contact address in its search (D-305 ruling 31): the server refuses the
// save 400 `contact_address_not_shareable` at `config.filters.q`, and that
// refusal is one form error with one string, the server's message key
// `views.validation.contact_address_not_shareable`, wherever it lands (Save
// as new, Save, Manage's share). The search box carries no second line of
// its own (R3, PR-E4 wave 4).
//
// THE REVEAL GATE (R11). The rows the controller judges come from a passive
// observer of the list's own key, and on `?view=<id>` alone that key is the
// bare list's until the view applies, so a bare page already in the cache
// would be revealed and then replaced. The controller's `listEnabled` verdict
// is carried in state, starting closed, and while it is closed the page data,
// the settle and the observer's rows read as absent, as on the work-order
// list.
//
// The AUDIENCE is §4.1's role set (ga/fa/sales/finance/planner), narrower
// than the `sales.read` holder set — hooks/permissions.ts says why.
//
// EXPORT CSV (P3-D PR-D5, D-289 rulings 1, 3, 13). The page head's Export walks
// THIS book's read with the filters and sort on screen and saves every
// matching customer (components/export-csv.tsx; the columns are
// lib/exports.ts's). The credit limit is written only when this reader's
// payload carries it — the same §7 absence the credit cell renders. A view's
// hidden columns do not narrow the file (D-305 ruling 23).
//
// TAGS AND THE BULK BAR (P3-E2 PR E2-2a1, spec 09 §4.1's E2-2a1 annotation,
// D-323 rulings 5 to 7).
//   * A Tags column shows each customer's tags as neutral chips. It declares
//     `presence`, so it exists only on a page where some row carries a tag.
//   * The Tag filter writes `tag` (the wire's `tag_id`), a filter of the
//     `sales.customers` view grammar since TF1, and offers the active tags and
//     the archived ones still in use. A failed tag-list read says so under the
//     filters with its request id and Retry. The export names the filter by
//     the tag's name and waits while that name is loading. A tag the `in_use`
//     read cannot name (archived, carried by archived customers alone, still
//     filtering through a view) is looked up in `archived=all`, and one that
//     is still unnamed prints as "Not in this list", never as its id.
//   * At 769 px and wider the book offers row selection and the bulk bar: Add
//     tag and Remove tag for a reader who may tag (§7 narrowing 22), Reassign
//     owner for a literal group_admin or facility_admin, Archive on
//     `perms.isAdmin`. The bar fans out one request per row through
//     `lib/bulk.ts`, which owns the keys and the outcomes; the screen holds
//     the run, says how it ended in one line above the table (and above the
//     empty state, when the run emptied the filtered book), keeps the refused
//     and failed rows selected, and offers Retry failed. A 401 stops the run
//     and sends the reader to sign in. A selected
//     row the archive would refuse (`has_live_business`) is marked with the
//     reason before the press. While offline the verbs are disabled. Below
//     769 px there is no selection and no bar; each customer's own page
//     carries its single verbs.
//   * A create's `X-Sales-Warning` is a list (`parseSalesWarnings`), each code
//     said in words; `POSSIBLE_DUPLICATE` never prints as a raw code.
//
// THE CANDIDATES PANEL AND IMPORT CUSTOMERS (P3-E2 PR E2-2c, spec 09 §4.1's
// E2-2c annotation, §3.13, D-324 rulings 1 to 3, D-325 ruling 11).
//   * New customer checks for duplicates after a pause in typing the names or
//     the tax id (components/duplicates/). The panel sits under the fields,
//     shows up to five candidates with Open or the sentence "N similar
//     customers you cannot open", and never blocks the create: nothing in it
//     can disable Create.
//   * The page head offers Import customers to a holder of
//     `onboarding.import.customers` (spec 02 §7), read off the onboarding
//     mirror because the code is module 02's. It opens §4.18 at
//     `/sales/customers/import`. The lane's gate is not the book's: a finance
//     reader reads the book and is not offered the import, and the import's
//     page states its own refusal for anyone who follows a link there.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../components/button";
import type {
  CellOption,
  CellWriteOutcome,
  TagsCellEditor,
  UserCellEditor,
} from "../../../../components/cell-editor";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { CheckboxField, Field } from "../../../../components/field";
import { PageHeader } from "../../../../components/breadcrumbs";
import { SalesFilterBar } from "../components/sales-filter-bar";
import { Modal } from "../../../../components/modal";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
  TableSkeleton,
} from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Timestamp, WholeRuns } from "../../../../components/whole-runs";
import { UnauthorizedError } from "../../../../lib/problem";
import { bookPager, useBookPages, useBookWindow } from "../hooks/book-pages";
import { newIdempotencyKey, RequestError, requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useSavedViews, ViewSwitcher, ViewsRegion } from "../../views";
import { customerFilterLabels } from "../lib/filter-labels";
import { reliabilityBand } from "../lib/reliability";
import { formatMinor, formatMinorRuns } from "../lib/money";
import { customersCsv, customersFilters } from "../lib/exports";
import { peekCode } from "../../../../components/peek-address";
import { NextStepCell } from "../components/pipeline/pipeline-parts";
import { SalesPeek, useSalesPeek } from "../components/peek/sales-peek";
import { MutationFailureNote } from "../mutation-failure-note";
import {
  SalesExportButton,
  SalesExportNotes,
  SalesExportSlot,
  useSalesExport,
} from "../components/export-csv";
import {
  canBulkArchive,
  canBulkReassign,
  canTagCustomers,
  isSalesReadRefused,
  useSalesPermissions,
} from "../hooks/permissions";
import { useMessagingPermissions } from "../../messaging/hooks/permissions";
import { useOnboardingPermissions } from "../../../onboarding/hooks/permissions";
import { DuplicateCandidatesPanel } from "../components/duplicates/duplicate-candidates";
import {
  compactSearch,
  CUSTOMER_STATUSES,
  CUSTOMER_TIERS,
  CUSTOMERS_DEFAULT_ORDER,
  isCustomersOrderToken,
  SALES_SEARCH_TEXT_MAX,
  validateSalesCustomersSearch,
  type CustomersOrderToken,
  type SalesCustomersSearch,
} from "./search-params";
import {
  useCreateCustomer,
  useCustomers,
  useOwnerIndex,
  type CustomerCreateRequest,
  type CustomersQuery,
  fetchCustomersPage,
} from "../hooks/sales-queries";
import { useMemberRefAsk, useReadsHeldOffline } from "../lib/reads";
import { useRefNamesSettled } from "../../../../lib/ref-names";
import { useDesktop } from "../../../../components/use-desktop";
import { TagChips } from "../components/tags/tag-chips";
import { TagFilter, tagFilterName } from "../components/tags/tag-filter";
import { addableTags, carriedTags } from "../components/tags/tag-options";
import {
  archiveCustomer,
  assignCustomerOwner,
  attachCustomerTag,
  detachCustomerTag,
  useInvalidateAfterBulk,
  useTagName,
  useTagOptions,
  type SalesTag,
} from "../hooks/crm-queries";
import {
  bulkSummary,
  executeBulk,
  hasFailedRows,
  rowsKeptSelected,
  startBulkRun,
  type BulkRun,
  type BulkVerb,
} from "../lib/bulk";

import { tierChipVariant } from "../lib/chips";
import "../components/merge/merge.css";

// The tier chip variant moved to `lib/chips.ts` (E2-1: the customer peek
// paints the same chip) and stays exported here, which the 360 imports.
export { tierChipVariant } from "../lib/chips";

const TIERS = CUSTOMER_TIERS;

/** The wire enum's ASC base keys — the type a column's `sortKey` may carry
 * (D-142: typed from the generated client, never a free string). Derived from
 * the full token union so a contract regeneration reshapes it automatically. */
type CustomersOrderKey = CustomersOrderToken extends `-${infer Base}` ? Base : CustomersOrderToken;

/** A row as `GET /v1/customers` serves it since P3-E2 PR E2-1: the customer
 * with its next step and the discipline flag (spec 09 §3.12, §5). */
type CustomerRow = components["schemas"]["CustomerWithNextStep"];

/** A row's open AR in minor units, or undefined when the list read did not
 * carry it. The list serves no AR while module 11 is absent (spec 09 §3.5b),
 * so this is undefined on every row today. `open_ar_minor` is §2's and §7's
 * name for the value, and module 11 adds it to the list item. */
function openArMinor(row: CustomerRow): number | undefined {
  const value = (row as { open_ar_minor?: unknown }).open_ar_minor;
  return typeof value === "number" ? value : undefined;
}

export function SalesCustomersScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const perms = useSalesPermissions();
  // §4.18's door (E2-2c): the import lane's own code, module 02's (spec 02
  // §7). Fail-closed like every mirror, so the button appears once the role
  // feed has answered, as New customer does.
  const mayImport = useOnboardingPermissions().canImport("customers");
  // The hint offers the phone only to a reader the server matches it for:
  // `q` leaves the address leg out of the query for anyone without
  // `messaging.address.read` (spec 09 §4.1/§7, D-307), as the inbox's `q`
  // does (#272). The grant is a messaging code, so it is read off the
  // messaging mirror, which is fail-closed: a custom role or a feed still in
  // flight is offered name, number and tax ID, which match for every reader.
  const searchHint = useMessagingPermissions().canReadAddress
    ? t("sales.customers.searchHintWithPhone")
    : t("sales.customers.searchHintNoPhone");

  // THE ADDRESS. Re-validated defensively: `strict: false` merges every
  // route's search types, and a shared link with a stale or foreign value
  // must still open the screen (the value is simply dropped).
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = useMemo(() => validateSalesCustomersSearch(rawSearch), [rawSearch]);
  const setSearch = (next: SalesCustomersSearch) => {
    void navigate({ to: "/sales/customers", search: compactSearch(next), replace: true });
  };

  const [creating, setCreating] = useState(false);
  const [warnings, setWarnings] = useState<
    { codes: readonly string[]; currency: string } | undefined
  >(undefined);
  // The bulk bar (E2-2a1): the selection, and the run in flight or the last
  // one, with the owners each row expected when it started (the reassign
  // sends `expected_owner_user_id`, and a retry must send the same body under
  // the same key).
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulk, setBulk] = useState<BulkState | undefined>(undefined);

  const owners = useOwnerIndex(perms.isAdmin);
  // A member directory the browser holds offline stays pending for the whole
  // cut, so the owner reads as its short id until it answers, the way the
  // 360 and the peek read it (#293).
  const directoryOffline = useReadsHeldOffline();
  const ownersResolving = owners.isPending && !directoryOffline;
  const memberRef = useMemberRefAsk(ownersResolving);
  // The side peek (spec 09 §4.15): a row's Peek button opens the customer
  // beside the book, and a row click still opens the 360.
  const peek = useSalesPeek();

  // The EFFECTIVE order — what the page is actually sorted by, and therefore
  // what the header carets/aria-sort must claim. An absent param IS the
  // server's `-created_at` default (D-242.3), so the Created column carries
  // descending aria-sort on a first paint with a bare URL.
  const order = search.order ?? CUSTOMERS_DEFAULT_ORDER;

  const query: CustomersQuery = useMemo(
    () => ({
      ...(search.q !== undefined ? { q: search.q } : {}),
      ...(search.tier !== undefined ? { tier: search.tier } : {}),
      ...(search.status !== undefined ? { status: search.status } : {}),
      ...(search.owner !== undefined ? { owner_user_id: search.owner } : {}),
      // Two-value string enum on the wire, never a coerced boolean (the
      // module-05 published mistake the contract itself restates as
      // `BoolQuery`); the ADDRESS holds the boolean the view config stores.
      ...(search.credit_hold !== undefined
        ? { credit_hold: search.credit_hold ? ("true" as const) : ("false" as const) }
        : {}),
      // The discipline flag (spec 09 §4.1's E2-1 annotation), `true` only.
      ...(search.needs_next_step === true ? { needs_next_step: "true" as const } : {}),
      // The Tag filter (E2-2a1, TF1): the tag's id as `tag_id`.
      ...(search.tag !== undefined ? { tag_id: search.tag } : {}),
      // Sent only when the URL carries it: an order-less request stays
      // byte-identical to the pre-D-242 wire shape the certified walkthroughs
      // were recorded against. Because `order` joins this memo, a sort change
      // resets the cursor stack and the fold through the same queryKey effect
      // as a filter change.
      ...(search.order !== undefined ? { order: search.order } : {}),
    }),
    [search],
  );

  // Keyed on WHAT the query asks, not the memo's identity — the M05-S08 race
  // (remnants.tsx carries the same fix with the full story).
  const queryKey = JSON.stringify(query);
  useEffect(() => {
    // The selection is of rows a query that no longer exists loaded (wave 1c:
    // the screen clears it with the cursor stack). A run in flight goes on.
    setSelected(new Set());
  }, [queryKey]);

  const refused = isSalesReadRefused(perms, perms.canOpenCustomers && perms.has("sales.read"));
  // One page at a time (D-560): the stack starts again at page 1 whenever
  // the query does.
  const pages = useBookPages(queryKey);
  const cursor = pages.cursor;

  // THE ROWS THE CONTROLLER JUDGES BY. The saved-views controller needs the
  // loaded rows (the chooser, `presence`, Modified) AND gates this very read
  // (`listEnabled`), so the rows are read from the list's cache entry through
  // a passive observer of the same key BEFORE the controller runs, and the
  // live read that fetches comes after it. Both observe one query, so within
  // a render they hold the same page. The fold is cache-synchronous, the
  // quote book's shape: `priorRows` holds the pages before the cursor and the
  // current page joins during render, so a warm remount paints its rows on
  // the first pass.
  //
  // THE REVEAL GATE (R11): the controller's verdict, carried in state and
  // starting closed. While it is closed the observer's page reads as absent,
  // since on `?view=<id>` alone it is the bare list's cached page, which the
  // applied read replaces.
  const [listGate, setListGate] = useState(false);
  const shown = useCustomers(query, cursor, false);
  const shownData = listGate ? shown.data : undefined;
  const rows = useMemo(() => shownData?.items ?? [], [shownData]);
  const turning = listGate && shown.isPlaceholderData;
  const pageView = useBookWindow(pages, {
    items: rows,
    nextCursor: shownData?.next_cursor,
    turning,
    settled: shownData !== undefined && !shown.isFetching,
  });

  // E2-2a1: who may press which bulk verb, and whether the bar exists at all
  // (never below 769 px, never for a refused reader).
  const desktop = useDesktop();
  const offline = useReadsHeldOffline();
  const mayTag = canTagCustomers(perms);
  const mayReassign = canBulkReassign(perms);
  const mayArchive = canBulkArchive(perms);
  const bulkOffered = desktop && !refused && (mayTag || mayReassign || mayArchive);
  // The tag list: the filter's options and the bar's pickers. A refused
  // reader is refused this read too, so it is not sent (D-088).
  const tagOptions = useTagOptions(!refused);
  // What a bulk run or a cell's write may have moved: the book, the 360s and
  // the quote book (hooks/crm-queries.ts).
  const invalidateAfterBulk = useInvalidateAfterBulk();

  // The columns read the directory as resolving only while it is in flight
  // online (#293); held offline, an owner reads as its short id.
  const columns = customerColumns(
    t,
    i18n.language,
    // A selected row the archive would refuse carries the reason before the
    // press (the #293 lesson), for a reader offered Archive.
    (row) =>
      bulkOffered &&
      mayArchive &&
      selected.has(row.id) &&
      row.status === "active" &&
      row.has_live_business,
    // E2-2d (spec 09 §4.1, D-326 ruling 3): Owner and Tags edit in place.
    customerCellEditors(t, {
      owners: { byId: owners.byId, isPending: ownersResolving },
      memberRef,
      tags: tagOptions.data ?? [],
      isAdmin: perms.isAdmin,
      mayTag,
      onSettled: invalidateAfterBulk,
      onSignedOut: () => {
        void navigate({ to: "/login" });
      },
    }),
  );

  const views = useSavedViews<SalesCustomersSearch, CustomerRow, CustomersOrderKey>({
    screenKey: "sales.customers",
    search,
    setSearch,
    validate: validateSalesCustomersSearch,
    columns,
    rows,
    rowsLoading: !listGate || shown.isFetching,
    // A refused stored filter is named in this bar's own words (V5).
    filterLabels: customerFilterLabels(t),
  });
  if (listGate !== views.listEnabled) setListGate(views.listEnabled);

  const live = useCustomers(query, cursor, !refused && listGate);
  // The live read as the screen sees it: nothing while the gate is closed.
  const page = {
    data: listGate ? live.data : undefined,
    error: listGate ? live.error : null,
    isPending: !listGate || live.isPending,
    isFetching: listGate && live.isFetching,
    fetchStatus: listGate ? live.fetchStatus : "idle",
    refetch: live.refetch,
  };

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  // THE SEARCH BOX. Its text is the reader's while they type; the debounced
  // value is written to the address, and the box follows the address when
  // anything else moves `q`. `qWritten` is the `q` the box itself last
  // wrote, so the address catching up with a write never snaps the box back
  // over keystrokes typed since.
  const [qText, setQText] = useState(search.q ?? "");
  const debouncedQ = useDebouncedValue(qText.trim(), 250);
  const qWritten = useRef(search.q);
  useEffect(() => {
    if (search.q === qWritten.current) return;
    qWritten.current = search.q;
    setQText(search.q ?? "");
  }, [search.q]);
  const writeRef = useRef(views.write);
  writeRef.current = views.write;
  const searchRef = useRef(search);
  searchRef.current = search;
  useEffect(() => {
    const now = searchRef.current;
    if (debouncedQ === (now.q ?? "").trim()) return;
    const next = debouncedQ.length > 0 ? debouncedQ : undefined;
    qWritten.current = next;
    writeRef.current({ ...now, q: next });
    // Keyed on the debounced text alone: this effect is an address WRITE, and
    // keying it on the address would re-fire it on its own navigation.
  }, [debouncedQ]);

  const setOrder = (next: string) => {
    // Tokens come from this screen's own column defs, but the write is still
    // gated: the URL only ever carries wire grammar. The default is written
    // as an ABSENT param so "back to newest-first" and first paint are the
    // same address.
    if (!isCustomersOrderToken(next)) return;
    views.write({ ...search, order: next === CUSTOMERS_DEFAULT_ORDER ? undefined : next });
  };

  /** Is a FILTER narrowing this book — not a sort? A filtered empty book asks
   * the reader to widen the filters, an unfiltered one to create a customer. */
  const isFiltered =
    search.q !== undefined ||
    search.tier !== undefined ||
    search.status !== undefined ||
    search.owner !== undefined ||
    search.credit_hold !== undefined ||
    search.needs_next_step !== undefined ||
    search.tag !== undefined;

  // The tag the address filters by, by name, for the export's line 2. While
  // the tag list has not named it the press waits (`namesPending`), so a file
  // never names a filter by a raw id. A tag the `in_use` list cannot name (an
  // archived tag only archived customers carry, still filtering through a
  // view, §9.43) is looked up in `archived=all`, and the press waits on that
  // too. A tag neither read names prints as "Not in this list".
  const listedTagName = tagFilterName(search.tag, tagOptions.data);
  const tagLookup = useTagName(
    search.tag,
    !refused && search.tag !== undefined && listedTagName === undefined && !tagOptions.isPending,
  );
  const tagName = listedTagName ?? tagLookup.data ?? undefined;
  const tagNamePending =
    search.tag !== undefined &&
    tagName === undefined &&
    (tagOptions.isPending || (tagLookup.isPending && tagLookup.fetchStatus !== "idle"));

  // The export walks the SAME read with the SAME filters (the address's `q`,
  // which the list was asked with, not the box's latest keystroke), plus the
  // EFFECTIVE sort token.
  const exporter = useSalesExport<CustomerRow>({
    list: "customers",
    resetKey: queryKey,
    fetchPage: (next, limit, signal) =>
      fetchCustomersPage({ ...query, order, limit }, next, signal),
    serialize: customersCsv,
    filters: customersFilters(t, {
      q: search.q ?? "",
      tier: search.tier ?? "",
      status: search.status ?? "",
      owner:
        search.owner !== undefined ? (owners.byId.get(search.owner) ?? search.owner) : undefined,
      creditHold: search.credit_hold === undefined ? "" : String(search.credit_hold),
      needsNextStep: search.needs_next_step === true,
      tag: search.tag !== undefined ? (tagName ?? t("sales.common.notListed")) : undefined,
    }),
    order,
    namesPending: tagNamePending,
  });

  // THE BULK RUN (spec 09 §4.1's E2-2a1 annotation). `lib/bulk.ts` mints one
  // key per (run, row) and classifies each answer; the screen sends, records
  // progress, keeps the refused and failed rows selected, and refreshes the
  // book once at the end (`invalidateAfterBulk`, above the columns).
  const rowsById = useMemo(() => new Map(rows.map((row) => [row.id, row])), [rows]);
  const rowsByIdRef = useRef(rowsById);
  rowsByIdRef.current = rowsById;
  const bulkAbort = useRef<AbortController | undefined>(undefined);
  useEffect(
    () => () => {
      bulkAbort.current?.abort();
    },
    [],
  );
  const bulkRunning = bulk?.running === true;

  const driveBulk = async (
    run: BulkRun,
    expectedOwners: ReadonlyMap<string, string | null>,
    mode: "first" | "retry",
  ) => {
    const controller = new AbortController();
    bulkAbort.current = controller;
    setBulk({ run, running: true, expectedOwners });
    const result = await executeBulk(
      run,
      {
        send: (rowId, key) => sendBulkRow(run, rowId, key, expectedOwners),
        rowState: (rowId) => rowsByIdRef.current.get(rowId),
        onProgress: (next) => {
          if (!controller.signal.aborted) setBulk({ run: next, running: true, expectedOwners });
        },
        signal: controller.signal,
      },
      mode,
    );
    if (controller.signal.aborted) return;
    setBulk({ run: result, running: false, expectedOwners });
    const kept = rowsKeptSelected(result);
    setSelected(new Set([...kept].filter((id) => rowsByIdRef.current.has(id))));
    invalidateAfterBulk();
    // The session ended mid-run: the rows after the 401 were never sent, and
    // the reader signs in again, as every read on this screen sends them.
    if (result.halted === "unauthorized") void navigate({ to: "/login" });
  };

  const startBulk = (verb: BulkVerb, target: string | undefined) => {
    const ids = rows.filter((row) => selected.has(row.id)).map((row) => row.id);
    if (ids.length === 0) return;
    const expected = new Map(ids.map((id) => [id, rowsById.get(id)?.owner_user_id ?? null]));
    void driveBulk(startBulkRun(verb, target, ids), expected, "first");
  };
  const selectedRows = rows.filter((row) => selected.has(row.id));

  const create = useCreateCustomer();

  // The region is revealed once the views read and the first page have both
  // settled (design-system §5, D-305 ruling 21). A refused reader has no list
  // to wait for, and a paused read is offline, which the region must show
  // rather than wait on forever.
  const paused = page.fetchStatus === "paused";
  const listSettled = page.data !== undefined || page.error !== null;
  // ...and once the owners the directory cannot name have been named by the
  // names read: the resolving marker is narrower than a name, so an owner
  // column revealed before the names land widens after first paint (D-456).
  const ownerNamesSettled = useRefNamesSettled(
    memberRef.kind,
    rows.flatMap((row) =>
      row.owner_user_id !== null && owners.byId.get(row.owner_user_id) === undefined
        ? [row.owner_user_id]
        : [],
    ),
  );
  const ready =
    refused || (views.settled && (listSettled || paused) && (ownerNamesSettled || paused));

  const chipLabel = (filter: string, value: string): string =>
    t("sales.common.filterChip", { filter, value });

  return (
    <section className="sales-customers-screen" data-testid="sales-customers-screen">
      <PageHeader
        title={t("sales.customers.title")}
        actions={
          <div className="sales-toolbar">
            {/* Offered once the first page has rows; until then the same control
              holds its place, inert and invisible, so the head never moves when
              the rows arrive (W2, PR-E4 wave 5). */}
            {refused ? null : rows.length > 0 ? (
              <SalesExportButton exporter={exporter} />
            ) : (
              <SalesExportSlot />
            )}
            {mayImport ? (
              <Button
                data-testid="sales-customers-import"
                onClick={() => {
                  void navigate({ to: "/sales/customers/import" });
                }}
              >
                {t("sales.customers.import")}
              </Button>
            ) : null}
            {perms.has("sales.manage_customers") ? (
              <Button
                variant="primary"
                data-testid="sales-customer-new"
                onClick={() => {
                  setCreating(true);
                }}
              >
                {t("sales.customers.new")}
              </Button>
            ) : null}
          </div>
        }
      />

      <SalesExportNotes exporter={exporter} />

      {/* The create's warnings, one line each, every code said in words
          (D-225); a code this client does not know is still said, never
          dropped. */}
      {warnings?.codes.map((code) => (
        <p
          key={code}
          className="sales-banner"
          role="status"
          data-code={code}
          data-testid={
            code === "NO_PRICE_LIST_FOR_CURRENCY"
              ? "sales-currency-warning"
              : code === "POSSIBLE_DUPLICATE"
                ? "sales-duplicate-warning"
                : "sales-warning-unknown"
          }
        >
          {code === "NO_PRICE_LIST_FOR_CURRENCY"
            ? t("sales.customers.currencyWarning", { currency: warnings.currency })
            : code === "POSSIBLE_DUPLICATE"
              ? t("sales.customers.warningPossibleDuplicate")
              : t("sales.customers.warningUnknown", { code })}
        </p>
      ))}

      <SalesFilterBar
        data-testid="sales-customers-filters"
        search={{
          value: qText,
          label: t("sales.customers.search"),
          placeholder: searchHint,
          "data-testid": "customers-q",
          onChange: (value) => {
            // Text holding U+0000 can live in no address and no view, so the
            // box empties as the address drops it (W8, PR-E4 wave 5).
            setQText(value.includes("\u0000") ? "" : value.slice(0, SALES_SEARCH_TEXT_MAX));
          },
        }}
        onClearAll={() => {
          views.write({
            ...search,
            tier: undefined,
            status: undefined,
            owner: undefined,
            credit_hold: undefined,
            needs_next_step: undefined,
            tag: undefined,
          });
        }}
        filters={[
          {
            key: "tier",
            inline: true,
            applied:
              search.tier === undefined
                ? undefined
                : chipLabel(t("sales.customers.filterTier"), t(`sales.tier.${search.tier}`)),
            onClear: () => {
              views.write({ ...search, tier: undefined });
            },
            control: (
              <Field label={t("sales.customers.filterTier")}>
                <Select
                  value={search.tier ?? ""}
                  data-testid="customers-tier"
                  onChange={(e) => {
                    views.write({ ...search, tier: TIERS.find((tier) => tier === e.target.value) });
                  }}
                >
                  <option value="">{t("sales.customers.anyTier")}</option>
                  {TIERS.map((value) => (
                    <option key={value} value={value}>
                      {t(`sales.tier.${value}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "needs_next_step",
            inline: true,
            applied:
              search.needs_next_step === true
                ? t("sales.customers.filterNeedsNextStep")
                : undefined,
            onClear: () => {
              views.write({ ...search, needs_next_step: undefined });
            },
            // §3.12's discipline flag: an active customer with live business
            // and no open task. A saved view stores it (D-314 ruling 9).
            control: (
              <CheckboxField
                label={t("sales.customers.filterNeedsNextStep")}
                checked={search.needs_next_step === true}
                data-testid="customers-needs-next-step"
                onChange={(checked) => {
                  views.write({ ...search, needs_next_step: checked ? true : undefined });
                }}
              />
            ),
          },
          {
            key: "status",
            applied:
              search.status === undefined
                ? undefined
                : chipLabel(
                    t("sales.customers.filterStatus"),
                    search.status === "active"
                      ? t("sales.customers.statusActive")
                      : t("sales.customers.statusArchived"),
                  ),
            onClear: () => {
              views.write({ ...search, status: undefined });
            },
            control: (
              <Field label={t("sales.customers.filterStatus")}>
                <Select
                  value={search.status ?? ""}
                  data-testid="customers-status"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      status: CUSTOMER_STATUSES.find((status) => status === e.target.value),
                    });
                  }}
                >
                  <option value="">{t("sales.customers.anyStatus")}</option>
                  <option value="active">{t("sales.customers.statusActive")}</option>
                  <option value="archived">{t("sales.customers.statusArchived")}</option>
                </Select>
              </Field>
            ),
          },
          {
            key: "owner",
            applied:
              search.owner === undefined
                ? undefined
                : chipLabel(
                    t("sales.customers.filterOwner"),
                    owners.byId.get(search.owner) ??
                      (ownersResolving ? t("ref.resolving") : t("sales.common.notListed")),
                  ),
            onClear: () => {
              views.write({ ...search, owner: undefined });
            },
            control: (
              <Field label={t("sales.customers.filterOwner")}>
                <Select
                  value={search.owner ?? ""}
                  data-testid="customers-owner"
                  onChange={(e) => {
                    views.write({
                      ...search,
                      owner: e.target.value.length > 0 ? e.target.value : undefined,
                    });
                  }}
                >
                  <option value="">{t("sales.customers.anyOwner")}</option>
                  {/* An owner the address names that the index does not hold (a
                      view an admin shared, read by a sales user whose index is
                      empty) still gets an option, so the control shows that a
                      filter is in force instead of reading "Any owner" over it. */}
                  {search.owner !== undefined && !owners.byId.has(search.owner) ? (
                    <option value={search.owner}>
                      {ownersResolving ? t("ref.resolving") : t("sales.common.notListed")}
                    </option>
                  ) : null}
                  {[...owners.byId.entries()].map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Select>
              </Field>
            ),
          },
          {
            key: "credit_hold",
            applied:
              search.credit_hold === undefined
                ? undefined
                : chipLabel(
                    t("sales.customers.filterCreditHold"),
                    search.credit_hold
                      ? t("sales.customers.creditHoldOn")
                      : t("sales.customers.creditHoldOff"),
                  ),
            onClear: () => {
              views.write({ ...search, credit_hold: undefined });
            },
            control: (
              <Field label={t("sales.customers.filterCreditHold")}>
                <Select
                  value={search.credit_hold === undefined ? "" : String(search.credit_hold)}
                  data-testid="customers-credit-hold"
                  onChange={(e) => {
                    const value = e.target.value;
                    views.write({
                      ...search,
                      credit_hold: value === "" ? undefined : value === "true",
                    });
                  }}
                >
                  <option value="">{t("sales.customers.anyCreditHold")}</option>
                  <option value="true">{t("sales.customers.creditHoldOn")}</option>
                  <option value="false">{t("sales.customers.creditHoldOff")}</option>
                </Select>
              </Field>
            ),
          },
          {
            key: "tag",
            applied:
              search.tag === undefined
                ? undefined
                : chipLabel(
                    t("sales.customers.filterTag"),
                    tagOptions.data?.find((tag) => tag.id === search.tag)?.name ??
                      t("sales.common.notListed"),
                  ),
            onClear: () => {
              views.write({ ...search, tag: undefined });
            },
            // E2-2a1 (TF1): one tag, stored by a saved view as its id.
            control: refused ? null : (
              <TagFilter
                value={search.tag}
                tags={tagOptions.data}
                pending={tagOptions.isPending && !offline}
                failed={tagOptions.isError}
                onChange={(tag) => {
                  views.write({ ...search, tag });
                }}
              />
            ),
          },
        ]}
      />

      {/* The tag list's own failure (spec 09 §4's error rule): one line with
          the request id and Retry, so the Tag filter's short list reads as a
          failed read and not as a tenant with no tags. */}
      {!refused && tagOptions.isError ? (
        <p className="sales-error-note" role="alert" data-testid="customers-tag-failed">
          {t("sales.tags.optionsFailed")}
          {requestIdOf(tagOptions.error) !== undefined ? (
            <>
              {" "}
              {t("sales.common.requestId")}: <code>{requestIdOf(tagOptions.error)}</code>
            </>
          ) : null}{" "}
          <Button
            size="sm"
            data-testid="customers-tag-retry"
            disabled={offline}
            busy={tagOptions.isFetching}
            busyLabel={t("sales.common.loading")}
            onClick={() => {
              void tagOptions.refetch();
            }}
          >
            {t("sales.common.retry")}
          </Button>
        </p>
      ) : null}

      <ViewsRegion ready={ready} cols={columns.length}>
        {!refused ? <ViewSwitcher views={views} /> : null}

        {refused ? (
          <ForbiddenState title={t("sales.access.title")} body={t("sales.access.customers")} />
        ) : null}

        {/* The run's one line stands above every state the book can be in,
            the empty one included: a Remove tag under that tag's filter, or
            an Archive under Status Active, empties the filtered book when it
            refreshes, and the reader must still read what the run did. */}
        {!refused && bulk !== undefined ? (
          <BulkOutcomeLine
            run={bulk.run}
            running={bulk.running}
            offline={offline}
            onRetry={() => {
              void driveBulk(bulk.run, bulk.expectedOwners, "retry");
            }}
            onDismiss={() => {
              setBulk(undefined);
            }}
          />
        ) : null}

        {!refused && paused && rows.length === 0 ? (
          <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
        ) : null}

        {!refused && page.isPending && !paused && rows.length === 0 ? (
          <TableSkeleton rows={5} cols={columns.length} label={t("sales.common.loading")} />
        ) : null}

        {!refused && page.error !== null && !(page.error instanceof UnauthorizedError) ? (
          <ErrorState
            title={t("sales.customers.error")}
            retryLabel={t("sales.common.retry")}
            requestIdLabel={t("sales.common.requestId")}
            requestId={requestIdOf(page.error)}
            onRetry={() => {
              void page.refetch();
            }}
          />
        ) : null}

        {!refused && !page.isPending && !paused && page.error === null && rows.length === 0 ? (
          // Two empties, because they ask for different acts: an empty BOOK is
          // "create one"; an empty FILTER (a view's, or the reader's own) is
          // "widen it".
          isFiltered ? (
            <EmptyState
              title={t("sales.customers.emptyFilteredTitle")}
              body={t("sales.customers.emptyFilteredBody")}
            />
          ) : (
            <EmptyState
              title={t("sales.customers.emptyTitle")}
              body={t("sales.customers.emptyBody")}
            />
          )
        ) : null}

        {rows.length > 0 ? (
          <>
            {rows.some((row) => openArMinor(row) !== undefined) ? null : (
              // §3.5b's degradation, said once for the book rather than in a
              // column of identical chips (D-336). It renders with the table,
              // so the first paint is the final one.
              <p className="sales-pending-note" data-testid="sales-ar-unavailable">
                <StatusChip
                  variant="neutral"
                  dot={false}
                  className="sales-ar-chip"
                  data-testid="sales-ar-chip"
                >
                  {t("sales.customers.arUnavailable")}
                </StatusChip>
              </p>
            )}
            <DataTable
              pager={bookPager(t, i18n.language, pageView, {
                turning,
                onNext: () => {
                  const next = shownData?.next_cursor;
                  if (next === null || next === undefined) return;
                  pages.next(next, rows.length);
                  setSelected(new Set());
                },
                onPrev: () => {
                  pages.prev();
                  setSelected(new Set());
                },
              })}
              data-testid="sales-customers-table"
              columns={columns}
              rows={rows}
              rowKey={(row) => row.id}
              empty={null}
              caption={t("sales.customers.tableCaption")}
              rowTestId="sales-customer-row"
              rowProps={(row) => ({
                "data-tier": row.tier,
                "data-status": row.status,
                "data-live-business": row.has_live_business ? "true" : "false",
              })}
              columnConfig={views.columnConfig}
              {...(bulkOffered
                ? {
                    selectable: {
                      selected,
                      onChange: setSelected,
                      headerLabel: t("sales.customers.selectAll"),
                      rowLabel: (row: CustomerRow) =>
                        t("sales.customers.selectRow", { customer: row.display_name }),
                      testId: "sales-customer-select",
                    },
                    // Spread, not a `| undefined` value: the bar is ABSENT
                    // while nothing is selected (`exactOptionalPropertyTypes`).
                    ...(selectedRows.length > 0
                      ? {
                          bulkBar: {
                            countLabel: t("sales.bulk.selected", { count: selectedRows.length }),
                            clearLabel: t("sales.bulk.clear"),
                            onClear: () => {
                              setSelected(new Set());
                            },
                            children: (
                              <CustomerBulkVerbs
                                selectedRows={selectedRows}
                                tags={tagOptions.data}
                                owners={owners.byId}
                                mayTag={mayTag}
                                mayReassign={mayReassign}
                                mayArchive={mayArchive}
                                disabled={bulkRunning || offline}
                                offline={offline}
                                onRun={startBulk}
                              />
                            ),
                          },
                        }
                      : {}),
                  }
                : {})}
              sort={{
                value: order,
                onChange: setOrder,
                // Called by the primitive per column and per next-direction, so
                // each caret's accessible name says what PRESSING it will do —
                // "Sort by 'Customer', descending" — instead of one generic
                // toggle label shared by every caret (the reader-plan shape).
                toggleLabel: (header: string, next: "ascending" | "descending") =>
                  next === "descending"
                    ? t("sales.customers.sortDesc", { column: header })
                    : t("sales.customers.sortAsc", { column: header }),
              }}
              keyboardNav
              // WRAP BEFORE WIDEN (design-system §3.3). Nine columns are shown
              // by default since E2-1 added Next step and the Peek button, and
              // in Russian their one-line labels and chips («Платёжная
              // дисциплина», «Встреча · Просрочено с 28 июн. 2026 г.») held the
              // book 1455 px wide at a 1440 px viewport, with the Created sort
              // control off the glass. `fit` lets a header label and a cell's
              // chip take a second line instead, so every column stays and
              // the book fits its box in every locale.
              fit
              peek={{
                key: (row) => peekCode("customer", row.id),
                current: peek.code,
                onOpen: (code) => {
                  peek.open(code);
                },
                onClose: peek.close,
                label: t("sales.peek.open"),
                rowLabel: (row) => t("sales.peek.openNamed", { name: row.display_name }),
              }}
              onRowClick={(row) => {
                void navigate({
                  to: "/sales/customers/$customerId",
                  params: { customerId: row.id },
                });
              }}
            />
          </>
        ) : null}
      </ViewsRegion>

      {creating ? (
        <NewCustomerModal
          busy={create.isPending}
          error={create.isError ? create.error : undefined}
          onClose={() => {
            create.reset();
            setCreating(false);
          }}
          onSubmit={(body, currency) => {
            create.mutate(body, {
              onSuccess: (result) => {
                setCreating(false);
                setWarnings(
                  result.warnings.length > 0
                    ? {
                        codes: result.warnings,
                        currency: currency ?? result.customer.default_currency,
                      }
                    : undefined,
                );
              },
            });
          }}
        />
      ) : null}

      <SalesPeek peek={peek} />
    </section>
  );
}

/**
 * §4.1's merged pointer (P3-E2 PR E2-2b, D-324 ruling 15). An archived row
 * whose customer was merged reads "Merged into" with the winner's CURRENT name
 * (`merged_into_display_name`, joined at read time, so a pointer a later merge
 * re-pointed names the new winner), and the row itself still opens the
 * tombstone. The name links to the winner only when `merged_into_openable`
 * holds, the reader's own customer door answering the winner; otherwise it is
 * plain text, so a collaborator the admin left off the merge, who keeps her
 * row on the loser (D-324 ruling 6), never follows a link to a 404. The link
 * stops its click at itself, or the row's own click would open the tombstone
 * over it. The list reads no customer per row: every fact is on the row.
 */
function MergedPointer({
  row,
  t,
}: {
  row: CustomerRow;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const winnerId = row.merged_into_customer_id ?? "";
  const name = row.merged_into_display_name ?? winnerId;
  return (
    <span
      className="sales-merged-pointer"
      data-testid="sales-customer-merged-into"
      data-winner={winnerId}
      data-openable={row.merged_into_openable === true ? "true" : "false"}
    >
      {t("sales.merge.listMergedInto")}{" "}
      {row.merged_into_openable === true ? (
        <Link
          to="/sales/customers/$customerId"
          params={{ customerId: winnerId }}
          data-testid="sales-customer-merged-into-link"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          {name}
        </Link>
      ) : (
        name
      )}
    </span>
  );
}

/**
 * The book's columns. CARETS ON THE ADJUDICATED COLUMNS ONLY (the header
 * comment carries the reasons per excluded column). `sortKey` is the ASC wire
 * token; the primitive derives the desc token and the aria-sort claim from
 * the table-level `sort.value`. The first column is the chooser's pinned lead
 * (D-305 ruling 9). Tags and Open AR declare `presence` (E2-2a1 and D-336).
 * Credit does not: the credit cell's §7 omission is per cell, and the column
 * carries the hold chip every reader is sent, so it is structural (ruling 8).
 * Owner and Tags take their cells from `editors` (E2-2d), which draw the value
 * at every width and edit it in place from 769 px up.
 */
function customerColumns(
  t: ReturnType<typeof useTranslation>["t"],
  language: string,
  marksLiveBusiness: (row: CustomerRow) => boolean,
  editors: CustomerCellEditors,
): DataTableColumn<CustomerRow, CustomersOrderKey>[] {
  return [
    {
      key: "display_name",
      header: t("sales.customers.colName"),
      id: true,
      sortKey: "display_name",
      render: (row) => {
        const name = marksLiveBusiness(row) ? (
          <span className="sales-name-cell">
            {row.display_name}
            <StatusChip
              variant="warn"
              dot={false}
              className="sales-live-business"
              data-testid="sales-live-business-mark"
            >
              {t("sales.bulk.liveBusinessChip")}
            </StatusChip>
          </span>
        ) : (
          row.display_name
        );
        // E2-2b (spec 09 §4.1's E2-2b annotation): a merged row names the
        // customer it went into, under the name.
        return (row.merged_into_customer_id ?? null) === null ? (
          name
        ) : (
          <span className="sales-name-cell">
            {name}
            <MergedPointer row={row} t={t} />
          </span>
        );
      },
    },
    {
      key: "customer_no",
      kind: "id",
      phonePriority: 1,
      header: t("sales.customers.colNo"),
      sortKey: "customer_no",
    },
    {
      // The tier is a chip, and a `status` cell stays on the phone card
      // without taking one of its three places, so the card keeps the tags
      // and the owner that M09-S31 reads there (spec 09 §4.1).
      key: "tier",
      kind: "status",
      header: t("sales.customers.colTier"),
      render: (row) => (
        <StatusChip variant={tierChipVariant(row.tier)} dot={false}>
          {t(`sales.tier.${row.tier}`)}
        </StatusChip>
      ),
    },
    {
      // E2-2a1 (spec 09 §4.1): the customer's tags as neutral chips. Present
      // only when a loaded row carries a tag, so an untagged tenant's book
      // gains no empty column, and a reader may hide it through the chooser.
      // E2-2d: the cell edits in place, and its chips are the editor's.
      key: "tags",
      phonePriority: 2,
      header: t("sales.customers.colTags"),
      presence: (rows) => rows.some((row) => row.tags.length > 0),
      editable: editors.tags,
    },
    {
      // E2-2d (D-326 ruling 3): the owner edits in place for a literal admin,
      // and reads as before, with the reason, for everyone else.
      key: "owner_user_id",
      phonePriority: 3,
      header: t("sales.customers.colOwner"),
      editable: editors.owner,
    },
    {
      key: "reliability_score",
      header: t("sales.customers.colReliability"),
      // First activation ASC deliberately: the actionable read of a payment-
      // discipline column is risk-first (lowest scores up top), and the wire
      // sorts NULLS LAST in both directions so unscored customers trail
      // rather than masquerading as the riskiest.
      sortKey: "reliability_score",
      render: (row) =>
        row.reliability_score === null ? (
          <RefLabel value={undefined} id={null} />
        ) : (
          <StatusChip
            variant={reliabilityBand(row.reliability_score)}
            data-testid="sales-reliability-chip"
            data-band={reliabilityBand(row.reliability_score)}
            data-provisional={row.reliability_provisional ? "true" : "false"}
          >
            {/* One inline run, so under `fit` the qualifier may take the
                chip's second line at the <wbr>: a chip's flex items never
                wrap, and «100 предварительно» on one line held the
                Reliability column 36 px wider in Russian. The text is
                unchanged, since <wbr> adds none. */}
            <span>
              {row.reliability_score}
              {row.reliability_provisional ? (
                <>
                  <wbr />
                  <span className="sales-provisional">{t("sales.customers.provisional")}</span>
                </>
              ) : null}
            </span>
          </StatusChip>
        ),
    },
    {
      // §3.5b/§4.1 (fixed after E2-2b, D-336): open AR is a PRESENCE column.
      // While module 11 is absent the list read carries no `open_ar_minor`
      // on any row, so the column is absent and the book states the
      // degradation once, in the line above the table. The day a row carries
      // a value the column returns by itself, and a row without one shows
      // the absent mark.
      key: "open_ar",
      kind: "money",
      header: t("sales.customers.colOpenAr"),
      presence: (rows) => rows.some((row) => openArMinor(row) !== undefined),
      render: (row) => {
        const minor = openArMinor(row);
        return minor === undefined ? (
          <RefLabel value={undefined} id={null} />
        ) : (
          formatMinor(minor, row.default_currency, language)
        );
      },
    },
    {
      key: "credit_limit_minor",
      kind: "money",
      header: t("sales.customers.colCredit"),
      render: (row) => {
        if (row.credit_hold) {
          return (
            <StatusChip variant="bad" data-testid="sales-credit-hold-chip">
              {t("sales.customers.creditHold")}
            </StatusChip>
          );
        }
        if (row.credit_limit_minor === undefined) {
          // §7 omission — this role may not see the limit. Nothing, not a dash.
          return null;
        }
        if (row.credit_limit_minor === 0) {
          return (
            <StatusChip variant="warn" data-testid="sales-prepay-chip">
              {t("sales.customers.prepayOnly")}
            </StatusChip>
          );
        }
        // The amount and its currency as two whole runs, so under `fit` the
        // currency may stand under the amount (D-336): a ten-digit limit in
        // tenge is ordinary for a distributor.
        return (
          <WholeRuns
            runs={formatMinorRuns(row.credit_limit_minor, row.default_currency, language)}
          />
        );
      },
    },
    {
      // E2-1 (spec 09 §4.1, D-313 rulings 6 and 7): the next step's kind and
      // date, amber today and red overdue, or the flag "No next step". Every
      // reader of the customer is served it, whoever wrote the task, and it
      // never shows a task's words or its assignee (§3.12).
      key: "next_step",
      kind: "status",
      header: t("sales.customers.colNextStep"),
      render: (row) => (
        <NextStepCell next={row.next_activity} needsNextStep={row.needs_next_step} />
      ),
    },
    {
      // Ruling A: the trailing Created column. It renders the default
      // `-created_at` sort's aria-sort on first paint and is the way back to
      // newest-first after any other sort. `sortFirst: "desc"` because
      // "newest first" is the only reading anyone means by clicking it. On
      // the phone card this is an ordinary data-label row.
      key: "created_at",
      kind: "date",
      header: t("sales.customers.colCreated"),
      age: true,
      sortKey: "created_at",
      sortFirst: "desc",
      // The date and the time in two whole runs, so under `fit` the time may
      // take the second line rather than hold the book wider than the glass.
      render: (row) => <Timestamp iso={row.created_at} locale={language} />,
    },
    {
      // PR-E4 (spec 09 §4.1): Updated, hidden by default, shown through the
      // column chooser and sortable by `updated_at`, which was the one wire
      // token with no caret until a reader could choose the column.
      key: "updated_at",
      kind: "date",
      header: t("sales.customers.colUpdated"),
      age: true,
      sortKey: "updated_at",
      sortFirst: "desc",
      defaultHidden: true,
      render: (row) => <Timestamp iso={row.updated_at} locale={language} />,
    },
  ];
}

/** §3.1 step 1 — the create form. `country`/`default_currency` may be left
 * empty: the API defaults them to the tenant's values, and an empty control
 * that states its default beats a guessed value. */
function NewCustomerModal({
  busy,
  error,
  onClose,
  onSubmit,
}: {
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (body: CustomerCreateRequest, currency: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const [legalName, setLegalName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [tier, setTier] = useState<(typeof TIERS)[number]>("b2b");
  const [currency, setCurrency] = useState("");
  const [taxId, setTaxId] = useState("");

  const currencyClean = currency.trim().toUpperCase();
  const currencyValid = currencyClean.length === 0 || /^[A-Z]{3}$/u.test(currencyClean);

  return (
    <Modal
      heading={t("sales.create.title")}
      label={t("sales.create.title")}
      closeLabel={t("sales.common.close")}
      onClose={onClose}
      className="sales-modal"
      testId="sales-customer-create-modal"
    >
      <div className="sales-form">
        <Field label={t("sales.create.legalName")} wide>
          <input
            value={legalName}
            onChange={(e) => {
              setLegalName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.create.displayName")} wide>
          <input
            value={displayName}
            onChange={(e) => {
              setDisplayName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.create.tier")}>
          <Select
            value={tier}
            onChange={(e) => {
              setTier(e.target.value as (typeof TIERS)[number]);
            }}
          >
            {TIERS.map((value) => (
              <option key={value} value={value}>
                {t(`sales.tier.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("sales.create.currency")}>
          <input
            value={currency}
            placeholder={t("sales.create.currencyHint")}
            onChange={(e) => {
              setCurrency(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.create.taxId")}>
          <input
            value={taxId}
            onChange={(e) => {
              setTaxId(e.target.value);
            }}
          />
        </Field>
      </div>
      {/* §4.1's candidates panel (E2-2c): under the fields at every width, and
          never a reason for Create to be disabled. */}
      <DuplicateCandidatesPanel legalName={legalName} displayName={displayName} taxId={taxId} />
      {error !== undefined ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.create.failed")}
          error={error}
        />
      ) : null}
      <div className="sales-modal-actions">
        <Button onClick={onClose}>{t("sales.common.cancel")}</Button>
        <Button
          variant="primary"
          data-testid="sales-customer-create-submit"
          busy={busy}
          busyLabel={t("sales.create.creating")}
          disabled={
            legalName.trim().length === 0 || displayName.trim().length === 0 || !currencyValid
          }
          onClick={() => {
            onSubmit(
              {
                legal_name: legalName.trim(),
                display_name: displayName.trim(),
                tier,
                ...(currencyClean.length > 0 ? { default_currency: currencyClean } : {}),
                ...(taxId.trim().length > 0 ? { tax_id: taxId.trim() } : {}),
              },
              currencyClean.length > 0 ? currencyClean : undefined,
            );
          }}
        >
          {t("sales.create.submit")}
        </Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// the Owner and Tags cells (P3-E2 PR E2-2d, spec 09 §4.1's E2-2d annotation)
// ---------------------------------------------------------------------------

interface CustomerCellEditors {
  readonly owner: UserCellEditor<CustomerRow>;
  readonly tags: TagsCellEditor<CustomerRow>;
}

/** `extensions.current_owner_user_id` on `OWNER_CHANGED`, which the problem
 * carries at its top level as every extension does: an id, `null` for a
 * customer with no owner now, or undefined when the answer names none. */
function currentOwnerOf(error: RequestError): string | null | undefined {
  const problem = error.problem as Record<string, unknown> | undefined;
  const value = problem?.["current_owner_user_id"];
  return typeof value === "string" || value === null ? value : undefined;
}

/** A cell's write met a 401: the session ended, so no code names a refusal. */
const SIGNED_OUT = "UNAUTHENTICATED";

/** A refused write's code, or its status when the answer carried no problem. */
function refusedCode(error: RequestError): string {
  return error.code ?? String(error.httpStatus);
}

/**
 * The two cells the book edits in place (D-326 ruling 3), each over the
 * single-field write the bulk bar already sends, one request per commit under
 * a fresh key, because a cell is one press.
 *
 *   * OWNER is the assignments route with `role: owner` and the owner the cell
 *     showed as `expected_owner_user_id`. Only a literal group_admin or
 *     facility_admin may write it (`perms.isAdmin`, D-224 point 7), so for
 *     anyone else the cell shows the owner with that reason. `ASSIGNMENT_EXISTS`
 *     is settled ("already the owner"), and `OWNER_CHANGED` shows the owner
 *     the server names and says who it is.
 *   * TAGS is one attach or detach per changed tag through rows 8 and 9, for a
 *     reader who may tag (§7 narrowing 22) on an active customer, since both
 *     doors refuse an archived one 422. The checklist offers the active tags
 *     and the archived ones the cell shows, so an archived tag can be cleared
 *     and is never added.
 *
 * Every answer invalidates the book, the 360s, the quote book and the tag
 * list, the bulk bar's set, so the refetch confirms what the cell shows. A
 * 401 is no refusal of the change: the session has ended, so the cell puts
 * the old value back, says so, and the screen sends the reader to sign in, as
 * the bulk bar does (design-system §3.13).
 */
function customerCellEditors(
  t: ReturnType<typeof useTranslation>["t"],
  input: {
    owners: { byId: ReadonlyMap<string, string>; isPending: boolean };
    /** Asks the names read for an owner the directory cannot name. */
    memberRef: { readonly kind?: "user" };
    tags: readonly SalesTag[];
    isAdmin: boolean;
    mayTag: boolean;
    onSettled: () => void;
    onSignedOut: () => void;
  },
): CustomerCellEditors {
  const { owners, memberRef, isAdmin, mayTag } = input;
  const busyLabel = t("sales.common.saving");
  const ownerOptions: CellOption[] = [...owners.byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const ownerOutcome = (error: RequestError): CellWriteOutcome<string | null> => {
    const code = refusedCode(error);
    if (code === "ASSIGNMENT_EXISTS") return { status: "settled" };
    const current = code === "OWNER_CHANGED" ? currentOwnerOf(error) : undefined;
    if (current !== undefined) {
      const name = current === null ? undefined : owners.byId.get(current);
      const message =
        current === null
          ? t("sales.cells.owner.changedToNone")
          : name !== undefined
            ? t("sales.cells.owner.changed", { owner: name })
            : t("sales.cells.owner.changedUnnamed");
      return { status: "changed", current, code, message };
    }
    return {
      status: "refused",
      code,
      message: t("sales.cells.owner.refused", { reason: refusalReason(t, code) }),
    };
  };

  const tagRef = (id: string, row: CustomerRow): CustomerRow["tags"][number] | undefined =>
    row.tags.find((tag) => tag.id === id) ?? input.tags.find((tag) => tag.id === id);

  const refusedTag = (tag: string, code: string): string =>
    BULK_REASON_CODES.has(code)
      ? t("sales.cells.tags.refusedItem", { tag, reason: refusalReason(t, code), code })
      : t("sales.cells.tags.refusedCode", { tag, code });

  return {
    owner: {
      kind: "user",
      testId: "sales-customer-owner-cell",
      writable: () => isAdmin,
      reason: () => (isAdmin ? undefined : t("sales.cells.owner.adminOnly")),
      editLabel: (row) => t("sales.cells.owner.edit", { name: row.display_name }),
      listLabel: (row) => t("sales.cells.owner.list", { name: row.display_name }),
      filterLabel: t("sales.cells.owner.filter"),
      noMatchLabel: t("sales.cells.owner.noMatch"),
      busyLabel,
      failedLabel: t("sales.cells.owner.failed"),
      value: (row) => row.owner_user_id,
      options: () => ownerOptions,
      display: (value) => (
        <RefLabel
          value={value === null ? undefined : owners.byId.get(value)}
          id={value}
          pending={owners.isPending}
          {...memberRef}
        />
      ),
      send: async (row, next, shown) => {
        try {
          await assignCustomerOwner(row.id, next, shown, newIdempotencyKey());
          return { status: "committed" };
        } catch (error) {
          if (error instanceof UnauthorizedError) {
            input.onSignedOut();
            return { status: "refused", code: SIGNED_OUT, message: t("sales.cells.signedOut") };
          }
          // A request that got no answer is the cell's own failure, in
          // `failedLabel`'s words.
          if (!(error instanceof RequestError)) throw error;
          return ownerOutcome(error);
        }
      },
      onSettled: input.onSettled,
    },
    tags: {
      kind: "tags",
      testId: "sales-customer-tags-cell",
      writable: (row) => mayTag && row.status === "active",
      reason: (row) =>
        !mayTag
          ? t("sales.tags.refusal.forbidden")
          : row.status !== "active"
            ? t("sales.tags.refusal.CUSTOMER_ARCHIVED")
            : undefined,
      editLabel: (row) => t("sales.cells.tags.edit", { name: row.display_name }),
      listLabel: (row) => t("sales.cells.tags.list", { name: row.display_name }),
      applyLabel: t("sales.cells.tags.apply"),
      busyLabel,
      failedLabel: t("sales.cells.tags.failed"),
      value: (row) => row.tags.map((tag) => tag.id),
      // An archived tag is offered only while the cell shows it, so a detach
      // the refetch has not confirmed yet never leaves it offered unticked.
      options: (row, shown) => {
        const offered = new Map<string, CellOption>();
        for (const tag of addableTags(input.tags)) {
          offered.set(tag.id, { value: tag.id, label: tag.name });
        }
        for (const id of shown) {
          const tag = tagRef(id, row);
          if (tag === undefined || offered.has(tag.id)) continue;
          offered.set(tag.id, {
            value: tag.id,
            label:
              tag.status === "archived"
                ? t("sales.cells.tags.archivedOption", { name: tag.name })
                : tag.name,
          });
        }
        return [...offered.values()];
      },
      display: (ids, row) => (
        <TagChips
          tags={ids
            .map((id) => tagRef(id, row))
            .filter((tag): tag is CustomerRow["tags"][number] => tag !== undefined)}
          data-testid="sales-customer-tags"
        />
      ),
      send: async (row, change) => {
        const key = newIdempotencyKey();
        try {
          await (change.attach
            ? attachCustomerTag(row.id, change.tag, key)
            : detachCustomerTag(row.id, change.tag, key));
          return { status: "committed" };
        } catch (error) {
          if (error instanceof UnauthorizedError) {
            input.onSignedOut();
            return { status: "refused", code: SIGNED_OUT, message: t("sales.cells.signedOut") };
          }
          if (!(error instanceof RequestError)) throw error;
          const code = refusedCode(error);
          const name = tagRef(change.tag, row)?.name ?? change.tag;
          return { status: "refused", code, message: refusedTag(name, code) };
        }
      },
      refusedLabel: (refused) =>
        t("sales.cells.tags.refused", {
          list: refused
            .map((r) =>
              r.code === undefined
                ? t("sales.cells.tags.noAnswer", { tag: r.option.label })
                : r.code === SIGNED_OUT
                  ? t("sales.cells.tags.signedOut", { tag: r.option.label })
                  : refusedTag(r.option.label, r.code),
            )
            .join(", "),
        }),
      onSettled: input.onSettled,
    },
  };
}

// ---------------------------------------------------------------------------
// the bulk bar (P3-E2 PR E2-2a1, spec 09 §4.1's E2-2a1 annotation)
// ---------------------------------------------------------------------------

interface BulkState {
  readonly run: BulkRun;
  readonly running: boolean;
  readonly expectedOwners: ReadonlyMap<string, string | null>;
}

/** One row's request under the run's key for that row. The reassign sends
 * the owner the row showed when the run started, so a retry under the same
 * key sends the same body. */
function sendBulkRow(
  run: BulkRun,
  rowId: string,
  key: string,
  expectedOwners: ReadonlyMap<string, string | null>,
): Promise<unknown> {
  const target = run.target ?? "";
  switch (run.verb) {
    case "add_tag":
      return attachCustomerTag(rowId, target, key);
    case "remove_tag":
      return detachCustomerTag(rowId, target, key);
    case "reassign_owner":
      return assignCustomerOwner(rowId, target, expectedOwners.get(rowId) ?? null, key);
    case "archive":
      return archiveCustomer(rowId, key);
  }
}

/** The bar's verbs. Each verb shows only to a reader who may press it, the
 * tag pickers offer only what the door would take, and Archive asks first. */
function CustomerBulkVerbs({
  selectedRows,
  tags,
  owners,
  mayTag,
  mayReassign,
  mayArchive,
  disabled,
  offline,
  onRun,
}: {
  selectedRows: readonly CustomerRow[];
  tags: readonly SalesTag[] | undefined;
  owners: ReadonlyMap<string, string>;
  mayTag: boolean;
  mayReassign: boolean;
  mayArchive: boolean;
  disabled: boolean;
  offline: boolean;
  onRun: (verb: BulkVerb, target: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const [tagId, setTagId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [confirmArchive, setConfirmArchive] = useState(false);
  // Add offers the active tags; Remove offers what the selected rows carry,
  // archived tags included. One picker serves both verbs.
  const addable = addableTags(tags ?? []);
  const carried = carriedTags(selectedRows);
  const pickable = [
    ...addable,
    ...carried.filter((tag) => !addable.some((option) => option.id === tag.id)),
  ];
  const picked = pickable.find((tag) => tag.id === tagId);
  const liveBusiness = selectedRows.filter(
    (row) => row.status === "active" && row.has_live_business,
  ).length;

  const verbs: ReactNode[] = [];
  if (mayTag) {
    verbs.push(
      <span key="tag" className="sales-bulk-group">
        <Select
          aria-label={t("sales.bulk.tagLabel")}
          value={tagId}
          data-testid="sales-bulk-tag-pick"
          disabled={disabled || tags === undefined}
          onChange={(e) => {
            setTagId(e.target.value);
          }}
        >
          <option value="">{t("sales.bulk.chooseTag")}</option>
          {pickable.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.status === "archived"
                ? t("sales.customers.tagArchivedOption", { name: tag.name })
                : tag.name}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          data-testid="sales-bulk-add-tag"
          disabled={disabled || picked?.status !== "active"}
          onClick={() => {
            onRun("add_tag", tagId);
          }}
        >
          {t("sales.bulk.addTag")}
        </Button>
        <Button
          size="sm"
          data-testid="sales-bulk-remove-tag"
          disabled={disabled || picked === undefined}
          onClick={() => {
            onRun("remove_tag", tagId);
          }}
        >
          {t("sales.bulk.removeTag")}
        </Button>
      </span>,
    );
  }
  if (mayReassign) {
    verbs.push(
      <span key="owner" className="sales-bulk-group">
        <Select
          aria-label={t("sales.bulk.ownerLabel")}
          value={ownerId}
          data-testid="sales-bulk-owner-pick"
          disabled={disabled || owners.size === 0}
          onChange={(e) => {
            setOwnerId(e.target.value);
          }}
        >
          <option value="">{t("sales.bulk.chooseOwner")}</option>
          {[...owners.entries()].map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Select>
        <Button
          size="sm"
          data-testid="sales-bulk-reassign"
          disabled={disabled || ownerId.length === 0}
          onClick={() => {
            onRun("reassign_owner", ownerId);
          }}
        >
          {t("sales.bulk.reassign")}
        </Button>
      </span>,
    );
  }
  if (mayArchive) {
    verbs.push(
      confirmArchive ? (
        <span key="archive" className="sales-bulk-group" role="group">
          <span className="sales-bulk-confirm" data-testid="sales-bulk-archive-confirm">
            {t("sales.bulk.archiveConfirm", { count: selectedRows.length })}
          </span>
          <Button
            size="sm"
            variant="danger"
            data-testid="sales-bulk-archive-yes"
            disabled={disabled}
            onClick={() => {
              setConfirmArchive(false);
              onRun("archive", undefined);
            }}
          >
            {t("sales.bulk.archiveYes")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setConfirmArchive(false);
            }}
          >
            {t("sales.common.cancel")}
          </Button>
        </span>
      ) : (
        <Button
          key="archive"
          size="sm"
          variant="danger"
          data-testid="sales-bulk-archive"
          disabled={disabled}
          onClick={() => {
            setConfirmArchive(true);
          }}
        >
          {t("sales.bulk.archive")}
        </Button>
      ),
    );
  }
  return (
    <>
      {verbs}
      {mayArchive && liveBusiness > 0 ? (
        <span className="sales-hint" data-testid="sales-bulk-live-business">
          {t("sales.bulk.liveBusiness", { count: liveBusiness })}
        </span>
      ) : null}
      {offline ? (
        <span className="sales-hint" data-testid="sales-bulk-offline">
          {t("sales.bulk.offline")}
        </span>
      ) : null}
    </>
  );
}

/** A refusal code in the reader's words, or the generic phrase naming it. */
function refusalReason(t: ReturnType<typeof useTranslation>["t"], code: string): string {
  return BULK_REASON_CODES.has(code)
    ? t(`sales.bulk.reason.${code}`)
    : t("sales.bulk.reasonOther", { code });
}

const BULK_REASON_CODES: ReadonlySet<string> = new Set([
  "CUSTOMER_ARCHIVED",
  "CUSTOMER_HAS_OPEN_DOCS",
  "CUSTOMER_NOT_FOUND",
  "TAG_ARCHIVED",
  "TAG_NOT_FOUND",
  "OWNER_CHANGED",
  "OWNER_NOT_MEMBER",
  "PERMISSION_DENIED",
  "VALIDATION_FAILED",
]);

/** The run's one line: each outcome counted, each refusal reason named, and
 * Retry failed while failed rows remain (spec 09 §4.1's E2-2a1 annotation). */
function BulkOutcomeLine({
  run,
  running,
  offline,
  onRetry,
  onDismiss,
}: {
  run: BulkRun;
  running: boolean;
  offline: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const summary = bulkSummary(run);
  const parts: { key: string; text: string }[] = [];
  if (running) {
    parts.push({
      key: "working",
      text: t("sales.bulk.working", {
        done: summary.total - summary.pending,
        total: summary.total,
      }),
    });
  }
  if (summary.done > 0)
    parts.push({ key: "done", text: t("sales.bulk.outcomeDone", { count: summary.done }) });
  if (summary.already > 0) {
    parts.push({
      key: "already",
      text: t(`sales.bulk.already.${run.verb}`, { count: summary.already }),
    });
  }
  for (const refusal of summary.refused) {
    parts.push({
      key: `refused:${refusal.code}`,
      text: t("sales.bulk.outcomeRefused", {
        count: refusal.count,
        reason: refusalReason(t, refusal.code),
      }),
    });
  }
  if (summary.failed > 0) {
    parts.push({ key: "failed", text: t("sales.bulk.outcomeFailed", { count: summary.failed }) });
  }
  if (!running && summary.pending > 0) {
    parts.push({
      key: "pending",
      text: t("sales.bulk.outcomePending", { count: summary.pending }),
    });
  }
  return (
    <div
      className="sales-bulk-outcome"
      role="status"
      data-testid="sales-bulk-outcome"
      data-running={running ? "true" : "false"}
      data-verb={run.verb}
    >
      <strong className="sales-bulk-verb">{t(`sales.bulk.verb.${run.verb}`)}</strong>
      {parts.map((part) => (
        <span key={part.key} className="sales-bulk-part" data-part={part.key}>
          {part.text}
        </span>
      ))}
      {!running && hasFailedRows(run) ? (
        <Button size="sm" data-testid="sales-bulk-retry" disabled={offline} onClick={onRetry}>
          {t("sales.bulk.retryFailed")}
        </Button>
      ) : null}
      {!running ? (
        <Button size="sm" data-testid="sales-bulk-dismiss" onClick={onDismiss}>
          {t("sales.common.close")}
        </Button>
      ) : null}
    </div>
  );
}
